import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import type { ActiveDotProps } from 'recharts/types/util/types';
import GroupButtons from '../../common/GroupButtons';
import StaticSummaryGroup from './shared/StaticSummaryGroup';
import type { StaticSummaryGroupItem } from './shared/StaticSummaryGroup';
import type { VisualReactionRoundChartData, VisualReactionStatistics } from './shared/utils';
import Table from '@/components/common/Table';
import type { Column } from '@/components/common/Table/types';
import { useFullscreen } from '@/components/layouts/StackedLayout/StackedLayout';

interface VisualReactionResultProps {
    statistics: VisualReactionStatistics;
    summaryFormation?: number[];
}

interface rxnResultsRowData {
    round: string;
    averageTimeMs: number;
    accuracy: number;
    fastestTimeMs: number;
}

const maxTickCount = 10;

const VisualReactionResult = ({ statistics, summaryFormation = [3] }: VisualReactionResultProps) => {
    const { t } = useTranslation('assessmentResultManagementPage');
    const { isMobile } = useFullscreen();

    const [roundStatistics, setRoundStatistics] = useState<VisualReactionRoundChartData[]>(statistics.rounds);

    const lineChartData = useMemo(() => {
        if (roundStatistics.length > 1) {
            // Multiple rounds
            let combinedData: any[] = [];

            roundStatistics.forEach((round, roundIndex) => {
                const roundData = round.trials.map((trial, index) => ({
                    ...trial,
                    question: index + 1,
                    round: roundIndex + 1,
                    roundName: t(`details.visualReaction.roundName`, { roundIndex: roundIndex + 1 }),
                }));

                combinedData = [...combinedData, ...roundData];
            });

            return combinedData;
        } else {
            return (
                roundStatistics[0]?.trials.map((trial, index) => ({
                    ...trial,
                    question: index + 1,
                    round: 1,
                    roundName: t(`details.visualReaction.roundName`, { roundIndex: 1 }),
                })) || []
            );
        }
    }, [roundStatistics, t]);

    // Get max question number from the chart data
    const maxQuestion = useMemo(() => {
        if (roundStatistics.length > 0) {
            return roundStatistics[0].trials.length;
        }
        return 0;
    }, [roundStatistics]);

    const columns: Column<rxnResultsRowData>[] = useMemo(
        () => [
            {
                title: t('details.visualReaction.columns.round'),
                dataIndex: 'round',
                key: 'round',
                className: 'font-semibold',
            },
            {
                title: t('details.visualReaction.columns.averageTime'),
                dataIndex: 'averageTimeMs',
                key: 'averageTimeMs',
                render: (value: number) => t('details.visualReaction.ms', { time: value.toFixed(0) }),
            },
            {
                title: t('details.visualReaction.columns.accuracy'),
                dataIndex: 'accuracy',
                key: 'accuracy',
                render: (value: number) => t('details.visualReaction.percent', { percent: value.toFixed(2) }),
            },
        ],
        [t]
    );

    const renderRoundDetailTable = useMemo(() => {
        if (statistics.rounds?.length > 1) {
            const dataSource: rxnResultsRowData[] = statistics.rounds.map((round, index) => ({
                round: t('details.visualReaction.roundName', { roundIndex: index + 1 }),
                averageTimeMs: round.averageResponseTimeMs,
                accuracy: round.accuracy,
                fastestTimeMs: round.fastestTimeMs,
            }));

            dataSource.push({
                round: t('details.visualReaction.average'),
                averageTimeMs: Math.round(
                    dataSource.reduce((pre, data) => pre + data.averageTimeMs, 0) / dataSource.length
                ),
                accuracy: Number(
                    (dataSource.reduce((pre, data) => pre + data.accuracy, 0) / dataSource.length).toFixed(2)
                ),
                fastestTimeMs: Math.round(
                    dataSource.reduce((pre, data) => pre + data.fastestTimeMs, 0) / dataSource.length
                ),
            });

            return (
                <div className="mt-5 lg:mt-10">
                    <Table<rxnResultsRowData>
                        columns={columns}
                        dataSource={dataSource}
                        rowKey="key"
                        headCellClassName={() => {
                            return 'border-b border-b-neutral-300';
                        }}
                        rowClassName={rowIndex => {
                            if (rowIndex === dataSource.length - 1) {
                                return '!bg-neutral-50 !border-b-0';
                            }
                            return '';
                        }}
                        cellClassName={(columnIndex, rowIndex) => {
                            if (columnIndex > 0 && rowIndex < dataSource.length - 1) {
                                return '!text-neutral-500';
                            }
                            return 'font-semibold text-neutral-900';
                        }}
                        equalWidth
                    />
                </div>
            );
        }

        return null;
    }, [columns, statistics.rounds, t]);

    const buttonSelectionChanges = useCallback(
        (roundIndex: number, value: boolean, isAll: boolean) => {
            if (!isAll) {
                if (value) {
                    setRoundStatistics([
                        ...roundStatistics,
                        ...statistics.rounds.filter(r => r.roundIndex === roundIndex),
                    ]);
                } else {
                    setRoundStatistics([...roundStatistics.filter(r => r.roundIndex !== roundIndex)]);
                }
            } else {
                if (value) {
                    setRoundStatistics([...statistics.rounds]);
                } else {
                    setRoundStatistics([]);
                }
            }
        },
        [roundStatistics, statistics.rounds]
    );

    const isSingleLine = statistics.rounds.length === 1;

    const customLegend = useMemo(() => {
        return (
            <div className="flex min-w-1/3">
                {roundStatistics.length > 0 && (
                    <div>
                        <ul
                            className={`${isSingleLine ? 'mt-10 ml-2 grid gap-1 lg:mt-0 lg:ml-4' : 'mt-10 ml-2 grid gap-1 lg:flex lg:gap-6'} list-none flex-col flex-wrap justify-center lg:flex-row`}
                        >
                            <li key={`item-1`} className="flex items-center gap-2.5">
                                <span
                                    className={`inline-block size-2 rounded-lg`}
                                    style={{ backgroundColor: 'var(--color-teal-600)' }}
                                />
                                <span className="text-sm text-neutral-500">{t('details.visualReaction.correct')}</span>
                            </li>
                            <li key={`item-2`} className="flex items-center gap-2.5">
                                <span
                                    className={`inline-block size-2 rounded-lg`}
                                    style={{ backgroundColor: 'var(--color-red-500)' }}
                                />
                                <span className="text-sm text-neutral-500">
                                    {t('details.visualReaction.incorrect')}
                                </span>
                            </li>
                            <li key={`item-3`} className="flex items-center gap-2.5">
                                <span
                                    className={`inline-block size-2 rounded-lg`}
                                    style={{ backgroundColor: 'var(--color-amber-500)' }}
                                />
                                <span className="text-sm text-neutral-500">{t('details.visualReaction.missed')}</span>
                            </li>
                        </ul>
                    </div>
                )}
            </div>
        );
    }, [isSingleLine, roundStatistics.length, t]);

    const summaryItems: StaticSummaryGroupItem[] = useMemo(
        () => [
            {
                title: t('details.visualReaction.percent', { percent: statistics.accuracy.toFixed(2) }),
                description: t('details.visualReaction.accuracy'),
            },
            {
                title: t('details.visualReaction.ms', { time: statistics.averageResponseTimeMs.toFixed(0) }),
                description: t('details.visualReaction.averageTime'),
            },
            {
                title: t('details.visualReaction.ms', { time: statistics.fastestResponseTimeMs }),
                description: t('details.visualReaction.fastestTime'),
            },
        ],
        [statistics.accuracy, statistics.averageResponseTimeMs, statistics.fastestResponseTimeMs, t]
    );

    return (
        <div>
            <div className="mx-auto max-w-3xl">
                {/* Statistics */}
                {statistics.rounds?.length === 1 && (
                    <div className="mb-8 w-full">
                        <StaticSummaryGroup formation={summaryFormation} isMobile={isMobile} items={summaryItems} />
                    </div>
                )}

                {/* Results */}
                <div className="item-center sm:items-left flex w-full flex-col justify-center sm:flex-row sm:gap-4">
                    <div className="grid w-full gap-6">
                        {statistics.rounds?.length > 1 && (
                            <GroupButtons
                                className="w-full"
                                buttons={[
                                    {
                                        label: t('details.visualReaction.selectAll'),
                                        className: 'h-[44px]',
                                        onClick: () =>
                                            buttonSelectionChanges(
                                                -1,
                                                !(roundStatistics.length === statistics.rounds.length),
                                                true
                                            ),
                                        isSelected: roundStatistics.length === statistics.rounds.length,
                                    },
                                    ...statistics.rounds.map((round, index) => {
                                        const isRoundSelected = roundStatistics.find(
                                            stat => stat.roundIndex === round.roundIndex
                                        );

                                        return {
                                            label: t('details.visualReaction.roundName', { roundIndex: index + 1 }),
                                            className: 'h-[44px]',
                                            onClick: () => {
                                                buttonSelectionChanges(round.roundIndex, !isRoundSelected, false);
                                            },
                                            isSelected: !!isRoundSelected,
                                        };
                                    }),
                                ]}
                            />
                        )}
                        <ResponsiveContainer width="100%" height={500}>
                            <LineChart data={lineChartData} margin={{ top: 5, right: 10, left: 20 }}>
                                <CartesianGrid strokeDasharray="1 1" />
                                <XAxis
                                    dataKey="question"
                                    type="number"
                                    domain={[1, maxQuestion]}
                                    tickCount={maxQuestion > maxTickCount ? maxTickCount : maxQuestion}
                                    stroke="var(--color-neutral-300)"
                                    tick={{ fill: 'var(--color-neutral-500)', fontSize: 12 }}
                                    label={{
                                        value: t('details.visualReaction.xaxisLineName'),
                                        position: 'insideBottom',
                                        offset: -20,
                                        fontSize: '12px',
                                    }}
                                />
                                <YAxis
                                    stroke="var(--color-neutral-300)"
                                    tick={{ fill: 'var(--color-neutral-500)', fontSize: 12 }}
                                    label={{
                                        value: t('details.visualReaction.yaxisLineName'),
                                        angle: -90,
                                        position: 'insideLeft',
                                        offset: -5,
                                        dy: 40,
                                        fontSize: '12px',
                                    }}
                                />
                                <Legend
                                    verticalAlign={isSingleLine ? (isMobile ? 'bottom' : 'top') : 'bottom'}
                                    align={isSingleLine ? (isMobile ? 'center' : 'right') : undefined}
                                    content={customLegend}
                                    layout="vertical"
                                    wrapperStyle={
                                        isMobile
                                            ? {
                                                  width: '100%',
                                                  alignItems: 'flex-start',
                                              }
                                            : {}
                                    }
                                />
                                {roundStatistics.map((round, roundIndex) => (
                                    <Line
                                        key={`round-${round.roundIndex}`}
                                        type="linear"
                                        dataKey="responseTimeMs"
                                        data={lineChartData.filter(item => item.round === roundIndex + 1)}
                                        stroke="transparent"
                                        strokeWidth={0}
                                        dot={(props: ActiveDotProps) => {
                                            const { cx, cy, payload } = props;

                                            let fillColor = 'var(--color-teal-600)';
                                            if (payload.isMissedResponse) {
                                                fillColor = 'var(--color-amber-500)';
                                            } else if (!payload?.isCorrect) {
                                                fillColor = 'var(--color-red-500)';
                                            }

                                            return <circle cx={cx} cy={cy} r={4} fill={fillColor} stroke="none" />;
                                        }}
                                        name={t('details.visualReaction.roundName', { roundIndex: roundIndex + 1 })}
                                    />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                {renderRoundDetailTable}
            </div>
        </div>
    );
};

export default VisualReactionResult;
